// Onboarding API
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  SupplierInvitation,
  PendingInitialSupplier,
  PendingSupplierDetail,
  OnboardingDataset,
  DatasetSection,
  DatasetQuestion,
  FilledDataset,
  FilledDatasetValue,
  ScoreBreakdown,
  RiskAssessment,
  ScorePreview,
  WorkflowRiskAssessment,
  OnboardingWorkflow,
  OnboardingActivityLog,
  OnboardingComplianceDocument,
  RequiredDocument,
  CodeOfConduct,
  CodeOfConductAgreement,
  OnboardingDashboardStats,
  InvitationApprover,
  ApprovalConfidence,
  // Customer Portal Mode types
  CustomerInvitation,
  CustomerOnboardingDataset,
  CustomerOnboardingWorkflow,
  CustomerPortalDashboardStats,
} from './types';

export interface OnboardingConfig {
  id?: number;
  company?: number;
  company_name?: string;
  supplier_creation_level?: string;
  approval_level?: string;
  allow_self_approval: boolean;
}

export const onboardingAPI = {
  // Invitations
  getInvitations: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<SupplierInvitation>>> =>
    api.get('/onboarding/invitations/', { params }),

  getInvitation: (id: number): Promise<AxiosResponse<SupplierInvitation>> =>
    api.get(`/onboarding/invitations/${id}/`),

  createInvitation: (data: Partial<SupplierInvitation>): Promise<AxiosResponse<SupplierInvitation>> =>
    api.post('/onboarding/invitations/', data),

  updateInvitation: (id: number, data: Partial<SupplierInvitation>): Promise<AxiosResponse<SupplierInvitation>> =>
    api.patch(`/onboarding/invitations/${id}/`, data),

  sendInvitation: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/send/`),

  resendInvitation: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/resend/`),

  cancelInvitation: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/cancel/`),

  // Pre-invite approval workflow
  submitForApproval: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/submit_for_approval/`),

  approveToSend: (id: number, notes?: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/approve_to_send/`, { notes }),

  rejectApproval: (id: number, reason: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/invitations/${id}/reject_approval/`, { reason }),

  getPendingApprovals: (): Promise<AxiosResponse<PaginatedResponse<SupplierInvitation>>> =>
    api.get('/onboarding/invitations/pending_approvals/'),

  // Pending initial suppliers (pre-invite approval)
  getPendingInitialSuppliers: (): Promise<AxiosResponse<{ results: PendingInitialSupplier[] }>> =>
    api.get('/onboarding/pending-suppliers/'),

  getPendingSupplierDetail: (profileId: number): Promise<AxiosResponse<PendingSupplierDetail>> =>
    api.get(`/onboarding/pending-suppliers/${profileId}/`),

  // CR-4817: approve now accepts either OnboardingDataset IDs (template_ids) OR
  // QualificationTemplate IDs (qualification_template_ids) — the Pending Approvals
  // assign list merges both sources and the backend resolves each to the
  // appropriate supplier-facing record.
  approvePendingSupplier: (
    profileId: number,
    payload?: { templateIds?: number[]; qualificationTemplateIds?: number[] },
  ): Promise<AxiosResponse<{ status: string; invitation_id: number; questionnaires_assigned: number }>> => {
    const body: Record<string, number[]> = {};
    if (payload?.templateIds && payload.templateIds.length > 0) body['template_ids'] = payload.templateIds;
    if (payload?.qualificationTemplateIds && payload.qualificationTemplateIds.length > 0) {
      body['qualification_template_ids'] = payload.qualificationTemplateIds;
    }
    return api.post(`/onboarding/pending-suppliers/${profileId}/approve/`, body);
  },

  rejectPendingSupplier: (profileId: number, reason: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/pending-suppliers/${profileId}/reject/`, { reason }),

  verifyInvitation: (token: string): Promise<AxiosResponse<{ valid: boolean; company_name?: string; contact_name?: string; email?: string; customer_name?: string; error?: string }>> =>
    api.get(`/onboarding/verify/${token}/`),

  // Datasets (questionnaire templates)
  getDatasets: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<OnboardingDataset>>> =>
    api.get('/onboarding/datasets/', { params }),

  getDataset: (id: number): Promise<AxiosResponse<OnboardingDataset>> =>
    api.get(`/onboarding/datasets/${id}/`),

  createDataset: (data: Partial<OnboardingDataset>): Promise<AxiosResponse<OnboardingDataset>> =>
    api.post('/onboarding/datasets/', data),

  updateDataset: (id: number, data: Partial<OnboardingDataset>): Promise<AxiosResponse<OnboardingDataset>> =>
    api.patch(`/onboarding/datasets/${id}/`, data),

  deleteDataset: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/onboarding/datasets/${id}/`),

  cloneDataset: (id: number): Promise<AxiosResponse<OnboardingDataset>> =>
    api.post(`/onboarding/datasets/${id}/clone/`),

  publishDataset: (id: number): Promise<AxiosResponse<{ status: string; version?: string }>> =>
    api.post(`/onboarding/datasets/${id}/publish/`),

  /** CR-4156: Create a new version of a dataset (archives the old one) */
  createDatasetVersion: (id: number): Promise<AxiosResponse<{
    status: string;
    dataset: OnboardingDataset;
    previous_version: string;
  }>> =>
    api.post(`/onboarding/datasets/${id}/create_new_version/`),

  // Sections
  getSections: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<DatasetSection>>> =>
    api.get('/onboarding/sections/', { params }),

  getSection: (id: number): Promise<AxiosResponse<DatasetSection>> =>
    api.get(`/onboarding/sections/${id}/`),

  createSection: (data: Partial<DatasetSection>): Promise<AxiosResponse<DatasetSection>> =>
    api.post('/onboarding/sections/', data),

  updateSection: (id: number, data: Partial<DatasetSection>): Promise<AxiosResponse<DatasetSection>> =>
    api.patch(`/onboarding/sections/${id}/`, data),

  deleteSection: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/onboarding/sections/${id}/`),

  // Questions
  getQuestions: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<DatasetQuestion>>> =>
    api.get('/onboarding/questions/', { params }),

  getQuestion: (id: number): Promise<AxiosResponse<DatasetQuestion>> =>
    api.get(`/onboarding/questions/${id}/`),

  createQuestion: (data: Partial<DatasetQuestion>): Promise<AxiosResponse<DatasetQuestion>> =>
    api.post('/onboarding/questions/', data),

  updateQuestion: (id: number, data: Partial<DatasetQuestion>): Promise<AxiosResponse<DatasetQuestion>> =>
    api.patch(`/onboarding/questions/${id}/`, data),

  deleteQuestion: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/onboarding/questions/${id}/`),

  // Filled Datasets (responses)
  getFilledDatasets: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<FilledDataset>>> =>
    api.get('/onboarding/filled-datasets/', { params }),

  getFilledDataset: (id: number): Promise<AxiosResponse<FilledDataset>> =>
    api.get(`/onboarding/filled-datasets/${id}/`),

  assignQuestionnaire: (datasetId: number, supplierIds: number[]): Promise<AxiosResponse<{
    status: string;
    created_count: number;
    skipped_count: number;
    created_ids: number[];
    skipped_supplier_ids: number[];
  }>> =>
    api.post('/onboarding/filled-datasets/assign/', { dataset_id: datasetId, supplier_ids: supplierIds }),

  submitFilledDataset: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/filled-datasets/${id}/submit/`),

  approveFilledDataset: (id: number, comments?: string, approvalConfidence?: ApprovalConfidence): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/filled-datasets/${id}/approve/`, { comments, approval_confidence: approvalConfidence }),

  rejectFilledDataset: (id: number, reason: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/filled-datasets/${id}/reject/`, { reason }),

  requestMoreInfo: (id: number, feedback: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/filled-datasets/${id}/request_info/`, { feedback }),

  // Approval Status (multi-level)
  getApprovalStatus: (id: number): Promise<AxiosResponse<{
    total_levels: number;
    current_level: number | null;
    completed_levels: number;
    levels: Array<{
      level: number;
      decision: string;
      reviewer: string | null;
      decided_at: string | null;
      comments: string;
    }>;
    is_complete: boolean;
    is_rejected: boolean;
  }>> =>
    api.get(`/onboarding/filled-datasets/${id}/approval_status/`),

  // Score & Risk
  getScoreBreakdown: (id: number): Promise<AxiosResponse<ScoreBreakdown>> =>
    api.get(`/onboarding/filled-datasets/${id}/score_breakdown/`),

  getRiskAssessment: (id: number): Promise<AxiosResponse<RiskAssessment>> =>
    api.get(`/onboarding/filled-datasets/${id}/risk_assessment/`),

  previewScore: (id: number): Promise<AxiosResponse<ScorePreview>> =>
    api.get(`/onboarding/filled-datasets/${id}/preview_score/`),

  recalculateScore: (id: number): Promise<AxiosResponse<{ total_score: number; max_possible_score: number; score_percentage: number; passed: boolean; pass_score: number }>> =>
    api.post(`/onboarding/filled-datasets/${id}/recalculate_score/`),

  getWorkflowRiskAssessment: (id: number): Promise<AxiosResponse<WorkflowRiskAssessment>> =>
    api.get(`/onboarding/workflows/${id}/risk_assessment/`),

  calculateWorkflowRisk: (id: number): Promise<AxiosResponse<WorkflowRiskAssessment>> =>
    api.post(`/onboarding/workflows/${id}/calculate_risk/`),

  // Values/Answers
  saveAnswer: (data: Partial<FilledDatasetValue>): Promise<AxiosResponse<FilledDatasetValue>> =>
    api.post('/onboarding/values/', data),

  bulkSaveAnswers: (filledDatasetId: number, answers: Array<{ question_id: number; text_value?: string; number_value?: number; date_value?: string; boolean_value?: boolean; choice_values?: string[]; matrix_values?: Record<string, unknown>; signature_data?: string }>): Promise<AxiosResponse<{ status: string; saved_count: number; completion_percentage: number }>> =>
    api.post('/onboarding/values/bulk_save/', { filled_dataset_id: filledDatasetId, answers }),

  uploadQuestionFile: (formData: FormData): Promise<AxiosResponse<{ file_path: string; file_name: string }>> =>
    api.post('/onboarding/values/upload_file/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // CR-5362: per-question reviewer decision endpoints. The customer
  // reviewer uses these on each FilledDatasetValue while inspecting a
  // submitted questionnaire. The backend rolls these up so the
  // questionnaire-level approve action is gated on every value being
  // approved or approved_with_concerns.
  approveAnswer: (
    valueId: number,
    comment?: string,
  ): Promise<AxiosResponse<{ status: string; question_status: string; reviewed_by: string; reviewed_at: string | null; filled_dataset_status: string }>> =>
    api.post(`/onboarding/values/${valueId}/approve/`, { comment }),

  approveAnswerWithConcerns: (
    valueId: number,
    comment?: string,
  ): Promise<AxiosResponse<{ status: string; question_status: string; reviewed_by: string; reviewed_at: string | null; filled_dataset_status: string }>> =>
    api.post(`/onboarding/values/${valueId}/approve-with-concerns/`, { comment }),

  rejectAnswer: (
    valueId: number,
    comment?: string,
  ): Promise<AxiosResponse<{ status: string; question_status: string; reviewed_by: string; reviewed_at: string | null; filled_dataset_status: string }>> =>
    api.post(`/onboarding/values/${valueId}/reject/`, { comment }),

  // Workflows
  getWorkflows: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<OnboardingWorkflow>>> =>
    api.get('/onboarding/workflows/', { params }),

  getWorkflow: (id: number): Promise<AxiosResponse<OnboardingWorkflow>> =>
    api.get(`/onboarding/workflows/${id}/`),

  submitWorkflow: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/workflows/${id}/submit/`),

  approveWorkflow: (id: number, approvalConfidence?: ApprovalConfidence): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/workflows/${id}/approve/`, { approval_confidence: approvalConfidence }),

  declineWorkflow: (id: number, reason?: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/workflows/${id}/decline/`, { reason }),

  getOnboardingConfig: (): Promise<AxiosResponse<OnboardingConfig>> =>
    api.get('/onboarding/config/'),

  updateOnboardingConfig: (data: Partial<OnboardingConfig>): Promise<AxiosResponse<OnboardingConfig>> =>
    api.patch('/onboarding/config/', data),

  getWorkflowTimeline: (id: number): Promise<AxiosResponse<OnboardingActivityLog[]>> =>
    api.get(`/onboarding/workflows/${id}/timeline/`),

  /**
   * CR-4477 round 2: aggregated audit feed across every onboarding workflow
   * for the given supplier, returned newest-first. Used by the Communication
   * Log tab on the supplier profile.
   */
  getSupplierAuditLog: (
    supplierCompanyId: number,
  ): Promise<AxiosResponse<{ count: number; results: OnboardingActivityLog[] }>> =>
    api.get('/onboarding/workflows/supplier-audit-log/', {
      params: { supplier: supplierCompanyId },
    }),

  /** CR-4474: Download approval certificate PDF for an approved workflow */
  downloadCertificate: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/onboarding/workflows/${id}/certificate/`, { responseType: 'blob' }),

  // Steps
  completeStep: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/steps/${id}/complete/`),

  // Documents
  getDocuments: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<OnboardingComplianceDocument>>> =>
    api.get('/onboarding/documents/', { params }),

  getDocument: (id: number): Promise<AxiosResponse<OnboardingComplianceDocument>> =>
    api.get(`/onboarding/documents/${id}/`),

  uploadDocument: (formData: FormData): Promise<AxiosResponse<OnboardingComplianceDocument>> =>
    api.post('/onboarding/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  verifyDocument: (id: number, notes?: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/documents/${id}/verify/`, { notes }),

  rejectDocument: (id: number, reason: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/documents/${id}/reject/`, { reason }),

  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/onboarding/documents/${id}/`),

  // Required Documents
  getRequiredDocuments: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<RequiredDocument>>> =>
    api.get('/onboarding/required-documents/', { params }),

  // Codes of Conduct
  getCodesOfConduct: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<CodeOfConduct>>> =>
    api.get('/onboarding/codes-of-conduct/', { params }),

  getCodeOfConduct: (id: number): Promise<AxiosResponse<CodeOfConduct>> =>
    api.get(`/onboarding/codes-of-conduct/${id}/`),

  createCodeOfConduct: (data: Partial<CodeOfConduct>): Promise<AxiosResponse<CodeOfConduct>> =>
    api.post('/onboarding/codes-of-conduct/', data),

  // Code of Conduct Agreements
  signCodeOfConduct: (data: { code_of_conduct_id: number; supplier_id: number; onboarding_id?: number; signatory_name: string; signatory_title: string; signatory_email?: string; signature_data?: string }): Promise<AxiosResponse<CodeOfConductAgreement>> =>
    api.post('/onboarding/coc-agreements/sign/', data),

  getAgreements: (params?: Record<string, unknown>): Promise<AxiosResponse<PaginatedResponse<CodeOfConductAgreement>>> =>
    api.get('/onboarding/coc-agreements/', { params }),

  // Dashboard
  getDashboard: (): Promise<AxiosResponse<OnboardingDashboardStats>> =>
    api.get('/onboarding/dashboard/'),

  // Risk Questions (dynamic, replaces hardcoded MAB questions)
  getRiskQuestions: (customerId: number): Promise<AxiosResponse<{
    dataset_id: number | null;
    dataset_name?: string;
    instructions?: string;
    questions: Array<{
      id: string;
      question_id: number;
      question: string;
      help_text?: string;
      type: string;
      options: string[];
      is_required: boolean;
      is_scored: boolean;
      max_score: string | null;
    }>;
  }>> =>
    api.get('/onboarding/datasets/risk-questions/', { params: { customer: customerId } }),

  // Wizard submission (wires Add Supplier wizard to modern onboarding)
  submitWizard: (data: {
    supplier_id: number;
    customer_id: number;
    risk_answers: Record<string, string>;
    risk_dataset_id: number | null;
    annual_spend: string | null;
    risk_questions?: Array<{
      id: string;
      question: string;
      type: string;
      options: string[];
      scores?: Record<string, number>;
      max_score?: number;
    }>;
  }): Promise<AxiosResponse<{
    workflow_id: number;
    status: string;
    onboarding_status: string;
    can_self_approve: boolean;
  }>> =>
    api.post('/onboarding/submit-wizard/', data),

  // Invitation Approvers
  getInvitationApprovers: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<InvitationApprover>>> =>
    api.get('/onboarding/invitation-approvers/', { params }),

  getMyApproverRoles: (): Promise<AxiosResponse<InvitationApprover[]>> =>
    api.get('/onboarding/invitation-approvers/my_approver_roles/'),

  // ============================================================================
  // CUSTOMER PORTAL MODE API
  // ============================================================================

  // Customer Invitations (supplier inviting customers)
  getCustomerInvitations: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<CustomerInvitation>>> =>
    api.get('/onboarding/customer-invitations/', { params }),

  getCustomerInvitation: (id: number): Promise<AxiosResponse<CustomerInvitation>> =>
    api.get(`/onboarding/customer-invitations/${id}/`),

  createCustomerInvitation: (data: Partial<CustomerInvitation>): Promise<AxiosResponse<CustomerInvitation>> =>
    api.post('/onboarding/customer-invitations/', data),

  updateCustomerInvitation: (id: number, data: Partial<CustomerInvitation>): Promise<AxiosResponse<CustomerInvitation>> =>
    api.patch(`/onboarding/customer-invitations/${id}/`, data),

  sendCustomerInvitation: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-invitations/${id}/send/`),

  cancelCustomerInvitation: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-invitations/${id}/cancel/`),

  submitCustomerInvitationForApproval: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-invitations/${id}/submit_for_approval/`),

  approveCustomerInvitation: (id: number, notes?: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-invitations/${id}/approve_to_send/`, { notes }),

  verifyCustomerInvitation: (token: string): Promise<AxiosResponse<{
    valid: boolean;
    status?: string;
    supplier_name?: string;
    invitee_company_name?: string;
    invitee_email?: string;
    message?: string;
    expires_at?: string;
    error?: string;
  }>> =>
    api.get(`/onboarding/customer/verify/${token}/`),

  acceptCustomerInvitation: (token: string): Promise<AxiosResponse<{
    status: string;
    workflow_id?: number;
    supplier_name?: string;
  }>> =>
    api.post(`/onboarding/customer/accept/${token}/`),

  // Customer Onboarding Datasets
  getCustomerDatasets: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<CustomerOnboardingDataset>>> =>
    api.get('/onboarding/customer-datasets/', { params }),

  getCustomerDataset: (id: number): Promise<AxiosResponse<CustomerOnboardingDataset>> =>
    api.get(`/onboarding/customer-datasets/${id}/`),

  createCustomerDataset: (data: Partial<CustomerOnboardingDataset>): Promise<AxiosResponse<CustomerOnboardingDataset>> =>
    api.post('/onboarding/customer-datasets/', data),

  updateCustomerDataset: (id: number, data: Partial<CustomerOnboardingDataset>): Promise<AxiosResponse<CustomerOnboardingDataset>> =>
    api.patch(`/onboarding/customer-datasets/${id}/`, data),

  deleteCustomerDataset: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/onboarding/customer-datasets/${id}/`),

  publishCustomerDataset: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-datasets/${id}/publish/`),

  // Customer Onboarding Workflows
  getCustomerWorkflows: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<CustomerOnboardingWorkflow>>> =>
    api.get('/onboarding/customer-workflows/', { params }),

  getCustomerWorkflow: (id: number): Promise<AxiosResponse<CustomerOnboardingWorkflow>> =>
    api.get(`/onboarding/customer-workflows/${id}/`),

  approveCustomerWorkflow: (id: number): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-workflows/${id}/approve/`),

  declineCustomerWorkflow: (id: number, reason: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/onboarding/customer-workflows/${id}/decline/`, { reason }),

  // Customer Portal Dashboard
  getCustomerPortalDashboard: (): Promise<AxiosResponse<CustomerPortalDashboardStats>> =>
    api.get('/onboarding/customer-portal/dashboard/'),
};
